import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Shield, Server, Lock, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Datenschutz = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-20 px-4 sm:px-6 lg:px-8">
      <div className="container mx-auto max-w-4xl">
        <Button 
          onClick={() => navigate('/')} 
          variant="outline"
          className="mb-8 border-orange-500 text-orange-500 hover:bg-orange-50"
        >
          <ArrowLeft size={18} className="mr-2" />
          Zurück zur Startseite
        </Button>

        <Card className="shadow-xl">
          <CardHeader className="border-b border-gray-200">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-red-100 rounded-lg flex items-center justify-center">
                <Shield className="text-orange-500" size={24} />
              </div>
              <CardTitle className="text-3xl font-bold text-gray-900">
                Datenschutzerklärung
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-8 space-y-8 text-gray-700 leading-relaxed">
            {/* Highlight Boxen */}
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <Server className="text-green-600 mb-2" size={24} />
                <h3 className="font-bold text-green-900">Server in DE</h3>
                <p className="text-sm text-green-700">100% Deutschland</p>
              </div>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <Lock className="text-blue-600 mb-2" size={24} />
                <h3 className="font-bold text-blue-900">DSGVO-konform</h3>
                <p className="text-sm text-blue-700">EU-Standards</p>
              </div>
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <Eye className="text-purple-600 mb-2" size={24} />
                <h3 className="font-bold text-purple-900">Transparent</h3>
                <p className="text-sm text-purple-700">Volle Kontrolle</p>
              </div>
            </div>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">1. Datenschutz auf einen Blick</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">Allgemeine Hinweise</h3>
              <p className="mb-4">
                Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen 
                Daten passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit 
                denen Sie persönlich identifiziert werden können. Ausführliche Informationen zum Thema Datenschutz 
                entnehmen Sie unserer unter diesem Text aufgeführten Datenschutzerklärung.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">Datenerfassung auf dieser Website</h3>
              <p className="mb-4">
                <strong>Wer ist verantwortlich für die Datenerfassung auf dieser Website?</strong><br />
                Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten 
                können Sie dem Impressum dieser Website entnehmen.
              </p>

              <p className="mb-4">
                <strong>Wie erfassen wir Ihre Daten?</strong><br />
                Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann es sich 
                z.B. um Daten handeln, die Sie in ein Kontaktformular eingeben.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">2. Hosting und Server-Standort</h2>
              <div className="p-6 bg-orange-50 border-l-4 border-orange-500 rounded mb-4">
                <p className="text-orange-900">
                  <strong className="flex items-center gap-2 mb-2">
                    <Server size={20} />
                    Wichtig für Sie:
                  </strong>
                  Alle unsere Server stehen ausschließlich in Deutschland. Ihre Daten verlassen niemals die 
                  Bundesrepublik Deutschland und unterliegen somit vollständig dem deutschen Datenschutzrecht 
                  und der DSGVO.
                </p>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">Hosting-Anbieter</h3>
              <p className="mb-4">
                Wir hosten die Inhalte unserer Website bei:<br /><br />
                <strong>1&1 IONOS SE</strong><br />
                Elgendorfer Str. 57<br />
                56410 Montabaur<br />
                Deutschland
              </p>
              <p className="mb-4">
                Die Server befinden sich ausschließlich in Deutschland. Sämtliche personenbezogenen Daten, 
                die auf dieser Website erfasst werden, werden auf diesen Servern gespeichert.
              </p>
              <p>
                Die Verwendung von 1&1 IONOS erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO. 
                Wir haben ein berechtigtes Interesse an einer möglichst zuverlässigen Darstellung unserer Website. 
                Sofern eine entsprechende Einwilligung abgefragt wurde, erfolgt die Verarbeitung ausschließlich 
                auf Grundlage von Art. 6 Abs. 1 lit. a DSGVO.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">3. Allgemeine Hinweise und Pflichtinformationen</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">Datenschutz</h3>
              <p className="mb-4">
                Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln 
                Ihre personenbezogenen Daten vertraulich und entsprechend der gesetzlichen Datenschutzvorschriften 
                sowie dieser Datenschutzerklärung.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">Hinweis zur verantwortlichen Stelle</h3>
              <p className="mb-4">
                Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:<br /><br />
                <strong>AppPilot</strong><br />
                Eugen Limon<br />
                Hölzlestraße 27<br />
                78315 Radolfzell am Bodensee<br />
                Deutschland<br /><br />
                E-Mail: info@apppilot.de<br />
                Telefon: +49 (0) 172 77 85 888
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">Widerruf Ihrer Einwilligung zur Datenverarbeitung</h3>
              <p className="mb-4">
                Viele Datenverarbeitungsvorgänge sind nur mit Ihrer ausdrücklichen Einwilligung möglich. Sie können 
                eine bereits erteilte Einwilligung jederzeit widerrufen. Dazu reicht eine formlose Mitteilung per 
                E-Mail an uns. Die Rechtmäßigkeit der bis zum Widerruf erfolgten Datenverarbeitung bleibt vom 
                Widerruf unberührt.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">4. Datenerfassung auf dieser Website</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">Cookies</h3>
              <p className="mb-4">
                Die Internetseiten verwenden teilweise so genannte Cookies. Cookies richten auf Ihrem Rechner 
                keinen Schaden an und enthalten keine Viren. Cookies dienen dazu, unser Angebot nutzerfreundlicher, 
                effektiver und sicherer zu machen. Cookies sind kleine Textdateien, die auf Ihrem Rechner abgelegt 
                werden und die Ihr Browser speichert.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">Kontaktformular</h3>
              <p className="mb-4">
                Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden folgende Daten erhoben:
              </p>
              <ul className="list-disc list-inside space-y-1 mb-4 ml-4">
                <li>Name (Pflichtfeld)</li>
                <li>E-Mail-Adresse (Pflichtfeld)</li>
                <li>Telefonnummer (optional)</li>
                <li>Unternehmen (optional)</li>
                <li>Nachricht (Pflichtfeld)</li>
              </ul>
              <p className="mb-4">
                Diese Daten werden zwecks Bearbeitung Ihrer Anfrage und für den Fall von Anschlussfragen bei uns 
                gespeichert. Die Daten werden an die E-Mail-Adresse info@apppilot.de weitergeleitet und für 
                einen Zeitraum von 12 Monaten gespeichert. Nach Ablauf dieser Frist werden die Daten gelöscht, 
                sofern keine gesetzlichen Aufbewahrungsfristen bestehen.
              </p>
              <p className="mb-4">
                Die Verarbeitung der in das Kontaktformular eingegebenen Daten erfolgt ausschließlich auf Grundlage 
                Ihrer Einwilligung (Art. 6 Abs. 1 lit. a DSGVO) bzw. zur Durchführung vorvertraglicher Maßnahmen 
                (Art. 6 Abs. 1 lit. b DSGVO). Ein Widerruf Ihrer bereits erteilten Einwilligung ist jederzeit möglich. 
                Für den Widerruf genügt eine formlose Mitteilung per E-Mail an info@apppilot.de.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">5. Externe Dienste und Auftragsverarbeiter</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">Google Fonts</h3>
              <p className="mb-4">
                Diese Website nutzt zur einheitlichen Darstellung von Schriftarten so genannte Google Fonts, die von 
                Google bereitgestellt werden. Beim Aufruf einer Seite lädt Ihr Browser die benötigten Fonts in ihren 
                Browsercache, um Texte und Schriftarten korrekt anzuzeigen.
              </p>
              <p className="mb-4">
                Zu diesem Zweck muss der von Ihnen verwendete Browser Verbindung zu den Servern von Google aufnehmen. 
                Hierdurch erlangt Google Kenntnis darüber, dass über Ihre IP-Adresse unsere Website aufgerufen wurde. 
                Die Nutzung von Google Fonts erfolgt im Interesse einer einheitlichen und ansprechenden Darstellung 
                unserer Online-Angebote. Dies stellt ein berechtigtes Interesse im Sinne von Art. 6 Abs. 1 lit. f DSGVO dar.
              </p>
              <p className="mb-4">
                Weitere Informationen zu Google Fonts finden Sie unter:<br />
                <a href="https://developers.google.com/fonts/faq" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:text-orange-600 underline">
                  https://developers.google.com/fonts/faq
                </a>
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-2">hCaptcha - Spam-Schutz</h3>
              <p className="mb-4">
                Wir verwenden den Dienst hCaptcha (im Folgenden "hCaptcha") auf unserer Website, um zu überprüfen, 
                ob die Eingaben auf unserem Kontaktformular von einem Menschen oder missbräuchlich von einem automatisierten, 
                maschinellen Verarbeiter vorgenommen werden. Der Dienst analysiert das Verhalten des Websitebesuchers und 
                überprüft verschiedene Merkmale.
              </p>
              <p className="mb-4">
                Anbieter: Intuition Machines, Inc., 345 California Street, Suite 600, San Francisco, CA 94104, USA<br />
                Datenschutzerklärung: <a href="https://www.hcaptcha.com/privacy" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:text-orange-600 underline">
                  https://www.hcaptcha.com/privacy
                </a>
              </p>
              <p className="mb-4">
                Die Verwendung von hCaptcha erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse 
                an der Verhinderung von Spam und Missbrauch). Die erhobenen Daten werden gelöscht, sobald sie für die 
                Zwecke, für die sie erhoben wurden, nicht mehr benötigt werden.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-2">Zervant - Rechnungs- und Angebotserstellung</h3>
              <p className="mb-4">
                Wir nutzen den Dienst Zervant zur Erstellung und Verwaltung von Angeboten und Rechnungen. 
                Hierbei werden folgende personenbezogene Daten an Zervant übermittelt:
              </p>
              <ul className="list-disc list-inside space-y-1 mb-4 ml-4">
                <li>Name und Anschrift des Kunden</li>
                <li>E-Mail-Adresse</li>
                <li>Angebotsdetails / Rechnungsdetails</li>
              </ul>
              <p className="mb-4">
                Anbieter: Zervant Oy, Mikonkatu 17 C, 00100 Helsinki, Finnland<br />
                Datenschutzerklärung: <a href="https://www.zervant.com/de/datenschutz/" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:text-orange-600 underline">
                  https://www.zervant.com/de/datenschutz/
                </a>
              </p>
              <p className="mb-4">
                Die Verwendung von Zervant erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung) 
                und Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an der effizienten Rechnungsverwaltung). 
                Mit Zervant wurde ein Auftragsverarbeitungsvertrag gemäß Art. 28 DSGVO geschlossen.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">6. Ihre Rechte</h2>
              <p className="mb-4">
                Sie haben jederzeit das Recht:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-4 ml-4">
                <li>unentgeltlich Auskunft über Ihre gespeicherten personenbezogenen Daten zu erhalten</li>
                <li>Berichtigung, Sperrung oder Löschung Ihrer Daten zu verlangen</li>
                <li>der Verarbeitung Ihrer Daten zu widersprechen</li>
                <li>sich bei der zuständigen Aufsichtsbehörde zu beschweren</li>
                <li>Ihre Daten in einem strukturierten, gängigen Format zu erhalten (Datenportabilität)</li>
              </ul>
              <p>
                Für alle Anfragen zu Ihren Rechten wenden Sie sich bitte an: info@apppilot.de
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">7. Datensicherheit</h2>
              <p className="mb-4">
                Wir verwenden innerhalb des Website-Besuchs das verbreitete SSL-Verfahren (Secure Socket Layer) in 
                Verbindung mit der jeweils höchsten Verschlüsselungsstufe, die von Ihrem Browser unterstützt wird. 
                In der Regel handelt es sich dabei um eine 256-Bit-Verschlüsselung.
              </p>
              <p>
                Wir bedienen uns geeigneter technischer und organisatorischer Sicherheitsmaßnahmen, um Ihre Daten 
                gegen zufällige oder vorsätzliche Manipulationen, teilweisen oder vollständigen Verlust, Zerstörung 
                oder gegen den unbefugten Zugriff Dritter zu schützen.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">8. Aktualität und Änderung dieser Datenschutzerklärung</h2>
              <p>
                Diese Datenschutzerklärung ist aktuell gültig und hat den Stand: {new Date().toLocaleDateString('de-DE', { year: 'numeric', month: 'long', day: 'numeric' })}.<br /><br />
                Durch die Weiterentwicklung unserer Website und Angebote darüber oder aufgrund geänderter gesetzlicher 
                beziehungsweise behördlicher Vorgaben kann es notwendig werden, diese Datenschutzerklärung zu ändern. 
                Die jeweils aktuelle Datenschutzerklärung kann jederzeit auf der Website unter diesem Link von Ihnen 
                abgerufen und ausgedruckt werden.
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Datenschutz;